<script setup>
import Layout from "../../layouts/main.vue";
import mushroom from "cem-primary-api";
import { onMounted, reactive, ref, computed, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";
import toastr from "toastr";
import MethodService from "@/service/MethodService";
import { Filter } from "mushroomjs";

const { t } = useI18n();

const route = useRoute();
const router = useRouter();
const loading = ref(false);
const serviceInfo = ref({});
const dpiList = ref([]);
const viewMode = ref("domain");
const totalCounts = reactive({
  domain: 0,
  ip: 0,
  port: 0,
  protocol: 0,
});
const viewOptions = computed(() => [
  { label: `Domain (${totalCounts.domain})`, value: "domain" },
  { label: `IP Address (${totalCounts.ip})`, value: "ip" },
  { label: `Port Only (${totalCounts.port})`, value: "port" },
  { label: `Protocol (${totalCounts.protocol})`, value: "protocol" },
]);
const showFormSearch = ref(false);
const dataSearch = reactive({
  dpi_type: "",
  iden_by: "",
  domain: "",
  ip_range: "",
  port: "",
  group_id: "",
  component_id: "",
});
const tableRules = reactive({
  page: 1,
  limit: 20,
  total: 0,
  offset: 0,
  lengthMenu: [10, 20, 50, 100],
});
const pageSizeDefault = ref(20);
const tableData = computed(() => dpiList.value);

const cleanSearchForm = (form) => {
  const clean = {};
  Object.keys(form).forEach((key) => {
    if (form[key] !== "" && form[key] !== null && form[key] !== undefined) {
      if (typeof form[key] === "string") {
        const val = form[key].trim();
        if (val !== "") clean[key] = val;
      } else {
        clean[key] = form[key];
      }
    }
  });
  return clean;
};

const dialogVisible = ref(false);
const dialogType = ref("add");
const formRef = ref(null);
const formData = reactive({
  id: null,
  iden_by: "Domain",
  domain: "",
  ip_range: "",
  port: "",
  protocol: "",
  service_id: null,
  component_id: null,
  group_id: null,
  service_group: "",
  component_name: "",
  dpi_type: "Manual",
});

const rules = computed(() => ({
  iden_by: [
    {
      required: true,
      message: t("t_iden_by"),
      trigger: "change",
    },
  ],
  domain: [
    { required: true, message: t("valid_required"), trigger: "blur" },
    {
      validator: (rule, value, callback) => {
        if (!value) {
          callback();
          return;
        }
        const trimmed = value.trim();
        if (trimmed !== value) {
          formData.domain = trimmed;
        }
        if (trimmed.length <= 3) {
          callback(new Error(t("t_domain_length")));
          return;
        }
        const valueWithoutNumber = trimmed.replace(/\[number\]/g, "");
        const allowedPattern = /^[a-zA-Z0-9.\-%]+$/;
        if (valueWithoutNumber && !allowedPattern.test(valueWithoutNumber)) {
          callback(new Error(t("t_domain_special_char")));
          return;
        }
        if (trimmed.endsWith("%")) {
          callback(new Error(t("t_domain_end_percent")));
          return;
        }
        if (trimmed.endsWith("[number]")) {
          callback(new Error(t("t_domain_end_number_tag")));
          return;
        }
        callback();
      },
      trigger: "blur",
    },
  ],
  ip_range: [
    { required: true, message: t("t_ip_address_invalid"), trigger: "blur" },
    {
      validator: (rule, value, callback) => {
        if (!value) return callback();

        const isValid =
          MethodService.isValidIPv4CIDR(value) ||
          MethodService.isValidIPv6CIDR(value);

        return isValid ? callback() : callback(new Error(t("t_ip_validate")));
      },
      trigger: "blur",
    },
  ],
  port: [
    {
      required: formData.iden_by === "port",
      message: t("valid_required"),
      trigger: "blur",
    },
    {
      validator: (rule, value, callback) => {
        if (!value) {
          callback();
          return;
        }
        const trimmed = value.trim();
        if (trimmed !== value) {
          formData.port = trimmed;
        }
        const pattern = /^\d+(\s*-\s*\d+)?(\s*,\s*\d+(\s*-\s*\d+)?)*$/;
        if (!pattern.test(trimmed)) {
          callback(new Error(t("t_port_validate")));
          return;
        }
        const parts = trimmed.split(",").map((s) => s.trim());
        for (const part of parts) {
          if (part.includes("-")) {
            const [startStr, endStr] = part.split("-").map((s) => s.trim());
            const start = Number(startStr);
            const end = Number(endStr);
            if (start < 1 || start > 65535 || end < 1 || end > 65535) {
              callback(new Error(t("t_port_out_of_range")));
              return;
            }
            if (start >= end) {
              callback(new Error(t("t_port_invalid_range")));
              return;
            }
          } else {
            const port = Number(part);
            if (port < 1 || port > 65535) {
              callback(new Error(t("t_port_out_of_range")));
              return;
            }
          }
        }
        callback();
      },
      trigger: "blur",
    },
  ],
  service_group: [
    { required: true, message: t("valid_requiredChange"), trigger: "change" },
  ],
  component_name: [
    { required: true, message: t("valid_requiredChange"), trigger: "change" },
  ],
}));

const categoryOptions = ref([]);
const componentOptions = ref([]);

const getCategories = async () => {
  try {
    const res = await mushroom.service_health_category.views.getListAsync({
      _limit: 1000,
    });
    categoryOptions.value = res.result || [];
  } catch (e) {
    console.error("Error fetching categories:", e);
  }
};

const getComponents = async () => {
  try {
    const res = await mushroom.service_health_component.views.getListAsync({
      _limit: 1000,
    });
    componentOptions.value = res.result || [];
  } catch (e) {
    console.error("Error fetching components:", e);
  }
};

const fetchServiceInfo = async () => {
  const id = parseInt(route.params.id);
  try {
    const res = await mushroom.service_health_ch.findByIdAsync({ id });
    if (res.result) {
      serviceInfo.value = res.result;
    }
  } catch (e) {
    console.error("Error fetching service info:", e);
  }
};

const changeData = async (data) => {
  if (!data || data.length === 0) return data;
  const idsComponent = [
    ...new Set(data.map((item) => item.component_id).filter((id) => id)),
  ];
  const idsCategory = [
    ...new Set(data.map((item) => item.group_id).filter((id) => id)),
  ];
  if (idsComponent.length > 0) {
    try {
      const res = await mushroom.service_health_component.views.getListAsync({
        filters: Filter.in("id", idsComponent).build(),
      });
      const components = res.result || [];
      const componentMap = {};
      components.forEach((c) => {
        componentMap[c.id] = c.name;
      });
      data.forEach((item) => {
        if (item.component_id && componentMap[item.component_id]) {
          item.component_name = componentMap[item.component_id];
        }
      });
    } catch (e) {
      console.error("Error in changeData:", e);
    }
  }
  if (idsCategory.length > 0) {
    try {
      const res = await mushroom.service_health_category.views.getListAsync({
        filters: Filter.in("group_id", idsCategory).build(),
      });
      const categories = res.result || [];
      const categoryMap = {};
      categories.forEach((c) => {
        categoryMap[c.group_id] = c.group_name;
      });
      data.forEach((item) => {
        if (item.group_id && categoryMap[item.group_id]) {
          item.service_group = categoryMap[item.group_id];
        }
      });
    } catch (e) {
      console.error("Error in changeData:", e);
    }
  }
  return data;
};

const fetchAllCounts = async () => {
  const serviceId = route.params.id;
  const searchParams = {
    service_id: serviceId,
    ...cleanSearchForm(dataSearch),
  };
  try {
    const [domainRes, ipRes, portRes, protocolRes] = await Promise.all([
      mushroom.service_health_dpi_domain.views.getCountAsync(searchParams),
      mushroom.service_health_dpi_ip.views.getCountAsync(searchParams),
      mushroom.service_health_dpi_port.views.getCountAsync(searchParams),
      mushroom.service_health_dpi_protocol.views.getCountAsync(searchParams),
    ]);
    totalCounts.domain = domainRes.result?.[0]?.total || 0;
    totalCounts.ip = ipRes.result?.[0]?.total || 0;
    totalCounts.port = portRes.result?.[0]?.total || 0;
    totalCounts.protocol = protocolRes.result?.[0]?.total || 0;
  } catch (e) {
    console.error("Error fetching all counts:", e);
  }
};

const fetchDpiListAsync = async () => {
  loading.value = true;
  const serviceId = route.params.id;
  const searchParams = {
    service_id: serviceId,
    ...cleanSearchForm(dataSearch),
    _limit: tableRules.limit,
    _offset: tableRules.offset,
  };

  try {
    let api;
    if (viewMode.value === "domain") api = mushroom.service_health_dpi_domain;
    else if (viewMode.value === "ip") api = mushroom.service_health_dpi_ip;
    else if (viewMode.value === "port") api = mushroom.service_health_dpi_port;
    else api = mushroom.service_health_dpi_protocol;

    const res = await api.views.getListAsync(searchParams);
    const resTotal = await api.views.getCountAsync(searchParams);
    dpiList.value = (await changeData(res.result)) || [];
    tableRules.total = resTotal.result?.[0]?.total || 0;
    totalCounts[viewMode.value] = tableRules.total;
  } catch (e) {
    console.error("Error fetching DPI list:", e);
  } finally {
    loading.value = false;
  }
};

const fn_tableSizeChange = async (limit) => {
  tableRules.limit = limit;
  pageSizeDefault.value = limit;
  tableRules.page = 1;
  tableRules.offset = 0;
  await fetchDpiListAsync();
};

const fn_tableCurentChange = async (page) => {
  tableRules.page = page;
  tableRules.offset = (page - 1) * tableRules.limit;
  await fetchDpiListAsync();
};

const fn_TimKiem = async () => {
  tableRules.page = 1;
  tableRules.offset = 0;
  await fetchDpiListAsync();
  await fetchAllCounts();
};

const fn_showFormTimKiem = () => {
  showFormSearch.value = !showFormSearch.value;
};

watch(viewMode, async () => {
  tableRules.page = 1;
  tableRules.offset = 0;
  await fetchDpiListAsync();
});

const handleAdd = () => {
  dialogType.value = "add";
  Object.assign(formData, {
    id: null,
    iden_by: viewMode.value,
    domain: "",
    ip_range: "",
    port: "",
    protocol: "",
    service_id: null,
    component_id: null,
    group_id: null,
    service_group: "",
    component_name: "",
    dpi_type: "Manual",
  });
  dialogVisible.value = true;
  nextTick(() => {
    if (formRef.value) formRef.value.clearValidate();
  });
};

const handleEdit = (row) => {
  dialogType.value = "edit";
  row.iden_by === "Port Only"
    ? (formData.id = row.port)
    : (formData.id = row.id);
  Object.assign(formData, { ...row });
  formData.iden_by = viewMode.value;
  dialogVisible.value = true;
  nextTick(() => {
    if (formRef.value) formRef.value.clearValidate();
  });
};

const handleDelete = (row) => {
  Swal.fire({
    title: t("t_warning"),
    text: t("t_delete_dpi_confirm"),
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#f46a6a",
    confirmButtonText: t("swal_accept"),
    cancelButtonText: t("swal_cancel"),
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        if (viewMode.value === "domain") {
          await mushroom.service_health_dpi_domain.deleteAsync({ id: row.id });
        } else if (viewMode.value === "ip") {
          await mushroom.service_health_dpi_ip.deleteAsync({ id: row.id });
        } else {
          await mushroom.service_health_dpi_port.deleteAsync({ id: row.port });
        }
        toastr.success(t("toastr_update_success"));
        await fetchDpiListAsync();
        await fetchAllCounts();
      } catch (e) {
        MethodService.showError(e.code);
      }
    }
  });
};

const handleComponentChange = (value) => {
  formData.component_id = value;
};

const handleCategoryChange = (value) => {
  formData.group_id = value;
};

const submitForm = async () => {
  if (!formRef.value) return;

  await formRef.value.validate(async (valid) => {
    if (valid) {
      loading.value = true;
      try {
        const payload = { ...formData };
        payload.service_id = route.params.id;

        let api;
        if (formData.iden_by === "domain")
          api = mushroom.service_health_dpi_domain;
        else if (formData.iden_by === "ip")
          api = mushroom.service_health_dpi_ip;
        else api = mushroom.service_health_dpi_port;

        if (dialogType.value === "add") {
          await api.createAsync(payload);
          toastr.success(t("toastr_add_success"));
        } else {
          if (payload.create_by === "System") {
            await api.createAsync(payload);
          } else await api.partialUpdateAsync(payload);
          toastr.success(t("toastr_update_success"));
        }
      } catch (e) {
        if (e?.subCode === 1002) {
          let duplicateMsg = "";
          if (formData.iden_by === "port") {
            duplicateMsg = t("t_port_duplicate");
          } else if (formData.iden_by === "domain") {
            duplicateMsg = t("t_domain_duplicate");
          } else {
            duplicateMsg = t("t_ip_address_duplicate");
          }
          const serviceName = e?.meta?.service?.service_name;
          const finalMsg = serviceName
            ? duplicateMsg + t("t_at_service", { service_name: serviceName })
            : duplicateMsg;
          toastr.error(finalMsg);
        } else if (e?.subCode === 1001) {
          if (formData.iden_by === "ip") toastr.error(t("t_ip_validate"));
          if (formData.iden_by === "port")
            toastr.error(t("t_port_invalid_range"));
        } else MethodService.showError(e.code);
      } finally {
        dialogVisible.value = false;
        await fetchDpiListAsync();
        await fetchAllCounts();
        loading.value = false;
      }
    }
  });
};

onMounted(async () => {
  await fetchServiceInfo();
  await fetchDpiListAsync();
  await fetchAllCounts();
  await getCategories();
  await getComponents();
});
</script>

<template>
  <Layout>
    <div class="card" style="min-height: 80vh">
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <div>
          <h3 class="text-uppercase mb-0">
            {{ $t("t_dpi_management") }}
            <span class="text-primary">{{ serviceInfo.service_name }}</span>
          </h3>
        </div>
        <div class="btn-header">
          <button
            class="btn btn-soft-secondary btn-border me-2"
            @click="router.go(-1)"
          >
            <i class="ri-arrow-left-line"></i> {{ $t("t_back") }}
          </button>
          <button
            class="btn btn-soft-secondary btn-border me-2"
            @click="showFormSearch = !showFormSearch"
          >
            <i class="ri-search-line"></i> {{ $t("t-search") }}
          </button>
          <button class="btn btn-secondary btn-border" @click="handleAdd">
            <i class="ri-add-circle-line"></i> {{ $t("t_add_dpi") }}
          </button>
        </div>
      </div>
      <div class="card-body" style="padding: 0 0 15px 0">
        <div class="main-body-search mx-3 mb-3">
          <b-collapse v-model="showFormSearch" id="dpiSearchCollapse">
            <b-card>
              <el-form label-position="top" :model="dataSearch">
                <b-row>
                  <b-col md="4">
                    <el-form-item :label="$t('t_dpi_type')">
                      <el-select
                        v-model="dataSearch.dpi_type"
                        :placeholder="$t('t_all')"
                        clearable
                        style="width: 100%"
                      >
                        <el-option label="System" value="System" />
                        <el-option label="Manual" value="Manual" />
                      </el-select>
                    </el-form-item>
                  </b-col>
                  <b-col md="4">
                    <el-form-item :label="$t('t_iden_by')">
                      <el-select
                        v-model="dataSearch.iden_by"
                        :placeholder="$t('t_all')"
                        clearable
                        style="width: 100%"
                      >
                        <el-option label="Domain" value="Domain" />
                        <el-option label="IP" value="IP" />
                        <el-option label="Port" value="Port" />
                        <el-option label="Protocol" value="Protocol" />
                      </el-select>
                    </el-form-item>
                  </b-col>
                  <b-col md="4">
                    <el-form-item :label="$t('t_domain')">
                      <el-input
                        v-model="dataSearch.domain"
                        :placeholder="$t('t_domain')"
                        clearable
                      />
                    </el-form-item>
                  </b-col>
                  <b-col md="4">
                    <el-form-item :label="$t('t_ip_address')">
                      <el-input
                        v-model="dataSearch.ip_range"
                        :placeholder="$t('t_ip_address')"
                        clearable
                      />
                    </el-form-item>
                  </b-col>
                  <b-col md="4">
                    <el-form-item :label="$t('t_port')">
                      <el-input
                        v-model="dataSearch.port"
                        :placeholder="$t('t_port')"
                        clearable
                      />
                    </el-form-item>
                  </b-col>
                  <b-col md="4">
                    <el-form-item :label="$t('t_category')">
                      <el-select
                        v-model="dataSearch.group_id"
                        :placeholder="$t('t_category')"
                        clearable
                        filterable
                        style="width: 100%"
                      >
                        <el-option
                          v-for="opt in categoryOptions"
                          :key="opt.id"
                          :label="opt.group_name"
                          :value="opt.group_id"
                        />
                      </el-select>
                    </el-form-item>
                  </b-col>
                  <b-col md="4">
                    <el-form-item :label="$t('t_component')">
                      <el-select
                        v-model="dataSearch.component_id"
                        :placeholder="$t('t_component')"
                        clearable
                        filterable
                        style="width: 100%"
                      >
                        <el-option
                          v-for="opt in componentOptions"
                          :key="opt.id"
                          :label="opt.name"
                          :value="opt.id"
                        />
                      </el-select>
                    </el-form-item>
                  </b-col>
                  <b-col md="4">
                    <el-form-item label="&nbsp;">  <div class="text-center">
                      <a
                        class="btn btn-soft-secondary btn-border waves-effect waves-light mx-3"
                        style="width: 80px"
                        @click="fn_showFormTimKiem()"
                      >
                        {{ $t("t-hide") }}
                      </a>
                      <button
                        class="btn btn-secondary bg-gradient btn-border waves-effect waves-light"
                        style="width: 100px"
                        type="button"
                        @click="fn_TimKiem()"
                      >
                        {{ $t("t-search") }}
                      </button>
                    </div></el-form-item>
                   
                  </b-col>
                </b-row>
              </el-form>
            </b-card>
          </b-collapse>
        </div>

        <div class="mx-3">
          <div class="mb-3 d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <el-segmented v-model="viewMode" :options="viewOptions" />
            </div>
          </div>

          <el-table
            v-loading="loading"
            :data="tableData"
            style="width: 100%"
            border
            size="small"
            max-height="550"
          >
            <el-table-column prop="" label="STT" width="60" align="center">
              <template #default="scope">
                <div class="text-center">
                  {{ tableRules.offset + scope.$index + 1 }}
                </div>
              </template>
            </el-table-column>
            <el-table-column
              prop="dpi_type"
              :label="$t('t_dpi_type')"
              width="110"
              align="center"
              sortable
            >
              <template #default="scope">
                <span
                  v-if="scope.row.dpi_type === 'System'"
                  class="badge badge-soft-danger"
                  >System</span
                >
                <span
                  v-else-if="scope.row.dpi_type === 'Manual'"
                  class="badge badge-soft-secondary"
                  >Manual</span
                >
                <span v-else class="badge badge-soft-info">{{
                  scope.row.dpi_type
                }}</span>
              </template>
            </el-table-column>
            <el-table-column
              prop="iden_by"
              :label="$t('t_iden_by')"
              width="100"
              align="center"
              sortable
            />
            <el-table-column
              prop="protocol"
              :label="$t('t_protocol')"
              width="100"
              align="center"
              sortable
              v-if="viewMode === 'protocol'"
            />
            <el-table-column
              v-if="viewMode === 'domain'"
              prop="domain"
              :label="$t('t_domain')"
              min-width="200"
              sortable
              align="center"
              show-overflow-tooltip
            >
              <template #default="scope">
                <div class="col-tooltip">
                  {{ scope.row.domain }}
                </div>
              </template></el-table-column
            >
            <el-table-column
              v-if="viewMode === 'ip'"
              prop="ip_range"
              :label="$t('t_ip_address')"
              min-width="150"
              sortable
              align="center"
              show-overflow-tooltip
            >
            </el-table-column>
            <el-table-column
              prop="port"
              :label="$t('t_port')"
              width="120"
              align="center"
              sortable
              v-if="viewMode !== 'protocol'"
            />

            <el-table-column
              prop="service_group"
              :label="$t('t_category')"
              min-width="130"
              sortable
              align="center"
              show-overflow-tooltip
            >
              <template #default="scope">
                <div class="col-tooltip">
                  {{ scope.row.service_group }}
                </div>
              </template></el-table-column
            >
            <el-table-column
              prop="component_name"
              :label="$t('t_component')"
              min-width="130"
              sortable
              align="center"
              show-overflow-tooltip
            >
              <template #default="scope">
                <div class="col-tooltip">
                  {{ scope.row.component_name }}
                </div>
              </template></el-table-column
            >
            <el-table-column
              prop="create_by"
              label="Create By"
              width="110"
              sortable
              align="center"
            >
              <template #default="scope">
                <span
                  v-if="scope.row.create_by === 'System'"
                  class="badge badge-soft-danger"
                >
                  System
                </span>
                <span
                  v-else-if="scope.row.create_by === 'Manual'"
                  class="badge badge-soft-secondary"
                >
                  Manual
                </span>
                <span v-else class="badge badge-soft-info">{{
                  scope.row.create_by
                }}</span>
              </template>
            </el-table-column>

            <el-table-column
              :label="$t('t-action')"
              width="120"
              align="center"
              fixed="right"
              v-if="viewMode !== 'protocol'"
            >
              <template #default="scope">
                <div class="btn-group-thao-tac">
                  <el-tooltip content="Sửa" placement="top">
                    <button
                      type="button"
                      class="btn btn-soft-success btn-sm me-1"
                      :disabled="scope.row.permit_edit === 0"
                      @click="handleEdit(scope.row)"
                    >
                      <i class="ri-edit-box-line"></i>
                    </button>
                  </el-tooltip>
                  <el-tooltip content="Xóa" placement="top">
                    <button
                      type="button"
                      class="btn btn-soft-danger btn-sm"
                      @click="handleDelete(scope.row)"
                      :disabled="
                        scope.row.create_by === 'System' ||
                        scope.row.permit_delete === 0
                      "
                    >
                      <i class="ri-delete-bin-6-line"></i>
                    </button>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>
          <div class="mt-3" style="float: right">
            <el-pagination
              small
              v-model:currentPage="tableRules.page"
              v-model:pageSize="pageSizeDefault"
              :page-sizes="tableRules.lengthMenu"
              background
              layout="total, sizes, prev, pager, next, jumper"
              :total="tableRules.total"
              @size-change="fn_tableSizeChange"
              @current-change="fn_tableCurentChange"
            />
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      v-model="dialogVisible"
      :title="dialogType === 'add' ? $t('t_add_dpi') : $t('t_edit_dpi') + '11'"
      width="500px"
      append-to-body
      destroy-on-close
    >
      <el-form
        ref="formRef"
        :model="formData"
        :rules="rules"
        label-position="top"
      >
        <el-form-item :label="$t('t_iden_by')" prop="iden_by">
          <el-radio-group
            v-model="formData.iden_by"
            :disabled="dialogType === 'edit'"
          >
            <el-radio-button label="domain">{{
              t("t_iden_by_domain")
            }}</el-radio-button>
            <el-radio-button label="ip">{{
              t("t_iden_by_ip")
            }}</el-radio-button>
            <el-radio-button label="port">{{
              t("t_iden_by_port_only")
            }}</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item
          :label="$t('t_domain')"
          prop="domain"
          v-if="formData.iden_by === 'domain'"
        >
          <el-input
            v-model="formData.domain"
            :placeholder="$t('t_place_domain')"
          />
        </el-form-item>

        <el-form-item
          :label="$t('t_ip_address')"
          prop="ip_range"
          v-if="formData.iden_by === 'ip'"
        >
          <el-input
            v-model="formData.ip_range"
            :placeholder="$t('t_place_ip_range')"
          />
        </el-form-item>

        <el-form-item :label="$t('t_port')" prop="port">
          <el-input
            v-model="formData.port"
            :placeholder="$t('t_place_port')"
            :disabled="dialogType === 'edit' && formData.iden_by === 'port'"
          />
        </el-form-item>

        <el-form-item :label="$t('t_category')" prop="service_group">
          <el-select
            v-model="formData.service_group"
            :placeholder="$t('t_category')"
            filterable
            style="width: 100%"
            @change="handleCategoryChange"
          >
            <el-option
              v-for="opt in categoryOptions"
              :key="opt.id"
              :label="opt.group_name"
              :value="opt.group_id"
            />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('t_component')" prop="component_name">
          <el-select
            v-model="formData.component_name"
            :placeholder="$t('t_component')"
            filterable
            style="width: 100%"
            @change="handleComponentChange"
          >
            <el-option
              v-for="opt in componentOptions"
              :key="opt.id"
              :label="opt.name"
              :value="opt.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="text-center">
          <button class="btn btn-secondary btn-border" @click="submitForm">
            {{ dialogType === "add" ? $t("t-add") : $t("t_update") }}
          </button>
        </div>
      </template>
    </el-dialog>
  </Layout>
</template>
